import { useState, useEffect, useMemo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { logFeature } from '../services/activity'
import { subscribeBenefitsCaptureDays, subscribeBenefitsCaptureStatus } from '../services/benefitsCapture'
import {
  foldPeriod, fetchFrom, latestYesterday, localDayOf,
  PERIOD_LABELS, DEFAULT_TIMEZONE,
  type PeriodKey, type PeriodResult, type ReasonKey,
  type BenefitsCaptureDayDoc, type BenefitsCaptureStatusDoc,
} from '../utils/benefitsCapturePeriod'
import './BenefitsCapture.css'

/**
 * Benefits Capture — the practice's scoreboard for how often the front desk
 * records a medical plan alongside vision (story 1, 2). The markup follows the
 * approved prototype section for section (header + period control, the hero
 * with its three stat rows, the four-tile funnel, the weekly chart with the
 * goal line, the by-clinic table beside the reasons list) on the app's light
 * ground. Every role sees the whole practice; every number is a sum of the
 * clinic-day docs the scheduled builder writes, folded in
 * `utils/benefitsCapturePeriod`. Counts, rates and clinic names only.
 */

interface BenefitsCaptureProps {
  orgId: string
  /** The practice's own front-desk goal (resolved; the default when unset). */
  frontDeskGoal: number
}

const DASH = '—'
const MINUS = '−'

/** The period as it reads inside a sentence ("of 640 check-ins · last 30 days"). */
const PERIOD_SENTENCE: Record<PeriodKey, string> = {
  '7d': 'last 7 days',
  '30d': 'last 30 days',
  sinceGoLive: 'since go-live',
}

/** The prior window as it reads in a delta ("+12 pts vs prior 30 days"). */
const PRIOR_SENTENCE: Record<PeriodKey, string> = {
  '7d': 'vs prior week',
  '30d': 'vs prior 30 days',
  sinceGoLive: '',
}

/** The seven rows' labels, and the hover text that says what the row means
 *  and whose job it is (the list is a to-do list; the row itself stays the
 *  prototype's bare label). */
const REASON_COPY: Record<ReasonKey, { label: string; hint: string }> = {
  patient_info: {
    label: 'Patient info mismatch',
    hint: 'Front desk: the payer rejected the check about the patient (member not found, DOB or subscriber mismatch), or the name or date of birth was missing. Confirm the card and the details at check-in.',
  },
  check_failed: {
    label: 'Check failed',
    hint: 'VitalRev: the check failed on our side or the payer\'s (an error, no coverage verdict, a configuration problem, a timeout). Retries are automatic; nothing for the front desk to do.',
  },
  provider: {
    label: 'Provider not set up',
    hint: 'Practice admin: the doctor on the appointment matches no provider and the clinic has no default one, so there is no NPI to check with. Add the doctor once under Doctors.',
  },
  payer: {
    label: 'Payer not recognized',
    hint: 'Front desk or VitalRev: we could not tell which company the insurance on file is. A corrected name at check-in, or a payer mapping, fixes it for good.',
  },
  no_medical_plan: {
    label: 'No medical plan on file',
    hint: 'Front desk: the only insurance on file is VSP or EyeMed. Ask for the medical card at check-in — this is the number the training moves.',
  },
  no_insurance: {
    label: 'No insurance on file',
    hint: 'Front desk: nothing on file for the visit. Ask for both cards at check-in.',
  },
  not_checked: {
    label: 'Not checked yet',
    hint: 'VitalRev: a medical plan is on file but no check has answered yet (never dispatched, or the row arrived after its date).',
  },
}

const EST_TIP = 'An estimate, not a count: vision-only check-ins multiplied by the share of medical checks that find active coverage. It assumes vision-only patients have active medical coverage as often as the patients we verified, which skews it high.'
const NO_BASELINE_TIP = 'No baseline is recorded for this practice.'

const fmt = (n: number) => n.toLocaleString('en-US')
const fmtPct = (p: number | null) => (p === null ? DASH : `${p}%`)
// A tile's share label ("96% of check-ins"); nothing when the share is
// undefined, so a tile with no visits shows its zero alone.
const shareLabel = (share: number | null, of: string) => (share === null ? null : `${share}% of ${of}`)

function fmtDay(day: string | null, withYear = true): string {
  if (!day) return DASH
  return new Date(`${day}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}),
  })
}

function fmtWeek(start: string, end: string): string {
  const s = new Date(`${start}T00:00:00`)
  const e = new Date(`${end}T00:00:00`)
  const sm = s.toLocaleDateString('en-US', { month: 'short' })
  const em = e.toLocaleDateString('en-US', { month: 'short' })
  return sm === em ? `${sm} ${s.getDate()}–${e.getDate()}` : `${sm} ${s.getDate()}–${em} ${e.getDate()}`
}

/** "+12 pts vs prior 30 days", "−31 vs prior week", "steady vs prior week",
 *  or a dash when no comparable prior window exists. */
function deltaText(value: number | null, period: PeriodKey, unit: 'pts' | 'visits' | 'bare'): string {
  const prior = PRIOR_SENTENCE[period]
  if (value === null || !prior) return DASH
  if (value === 0) return `steady ${prior}`
  const sign = value > 0 ? '+' : MINUS
  const n = fmt(Math.abs(value))
  const word = unit === 'pts' ? ` ${Math.abs(value) === 1 ? 'pt' : 'pts'}` : unit === 'visits' ? ' visits' : ''
  return `${sign}${n}${word} ${prior}`
}

// A "vs prior" line; nothing when there is no comparable prior window, so a
// number with no comparison is not stated as a dash.
function Delta({ text, className }: { text: string; className: string }) {
  if (text === DASH) return null
  return <span className={className}>{text}</span>
}

function EstBadge() {
  return <span className="bc-est" title={EST_TIP}>Est.</span>
}

function HeroStat({ value, label, delta, badge }: { value: ReactNode; label: string; delta: string; badge?: boolean }) {
  return (
    <div className="bc-hero__stat">
      <span className="bc-hero__stat-value">{value}{badge && <EstBadge />}</span>
      <span className="bc-hero__stat-text">
        <span className="bc-hero__stat-label">{label}</span>
        <Delta text={delta} className="bc-hero__stat-delta" />
      </span>
    </div>
  )
}

function Tile({ fill, bar, value, pctLabel, label, delta, note, noteAccent }: {
  fill: 'neutral' | 'neutral-2' | 'accent' | 'accent-2'
  bar: number | null
  value: number
  pctLabel: string | null
  label: string
  delta: string
  note: string
  noteAccent?: boolean
}) {
  return (
    <div className="bc-tile">
      <div className="bc-tile__track" aria-hidden="true">
        <div className={`bc-tile__fill bc-tile__fill--${fill}`} style={{ width: `${bar ?? 0}%` }} />
      </div>
      <div className="bc-tile__row">
        <span className="bc-tile__value">{fmt(value)}</span>
        {pctLabel && <span className="bc-tile__pct">{pctLabel}</span>}
      </div>
      <div className="bc-tile__label">{label}</div>
      <Delta text={delta} className="bc-tile__delta" />
      <div className={`bc-tile__note${noteAccent ? ' bc-tile__note--accent' : ''}`}>{note}</div>
    </div>
  )
}

// ── The weekly chart ──────────────────────────────────────────────────

function WeeklyChart({ weeks, goal }: { weeks: PeriodResult['weeks']; goal: number }) {
  // Nothing in the whole window: one quiet line instead of empty columns,
  // and no goal line to measure nothing against.
  if (!weeks.some((w) => w.visits > 0)) {
    return <div className="bc-chart bc-chart--empty">No visits</div>
  }
  return (
    <>
      <div className="bc-chart">
        <div className="bc-chart__line" style={{ bottom: `${goal}%` }} aria-hidden="true">
          <span className="bc-chart__line-label">Front-desk goal {goal}%</span>
        </div>
        <div className="bc-chart__bars" role="img" aria-label="Weekly share of check-ins with a medical answer">
          {weeks.map((w) => (
            <div key={w.start} className="bc-chart__col" title={`${fmtWeek(w.start, w.end)}: ${w.share === null ? 'no visits' : `${w.share}% of ${fmt(w.visits)} check-ins`}${w.partial ? ' (partial week)' : ''}`}>
              <span className={`bc-chart__value${w.share === null ? ' bc-chart__value--empty' : ''}`}>
                {w.share === null ? 'no visits' : `${w.share}%`}
              </span>
              {w.share !== null && (
                <div className={`bc-chart__bar${w.partial ? ' bc-chart__bar--current' : ''}`} style={{ height: `${w.share}%` }} />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="bc-chart__labels">
        {weeks.map((w) => (
          <span key={w.start} className="bc-chart__label">
            {fmtWeek(w.start, w.end)}
            {w.partial && <em className="bc-chart__partial">partial week</em>}
          </span>
        ))}
      </div>
    </>
  )
}

// ── The page ──────────────────────────────────────────────────────────

const EMPTY_STATUS: BenefitsCaptureStatusDoc = { clinics: [], goLiveDay: null }
const LOAD_ERROR = "Couldn't load your numbers, try again"

/** One delivery of the day docs, stamped with the clock its windows were
 *  computed from and the subscription it belongs to, so a stale snapshot
 *  from a previous period never renders as the current one. */
interface DaysSnapshot {
  key: string
  now: Date
  docs: BenefitsCaptureDayDoc[]
}

export default function BenefitsCapture({ orgId, frontDeskGoal }: BenefitsCaptureProps) {
  const [period, setPeriod] = useState<PeriodKey>('7d')
  const [status, setStatus] = useState<BenefitsCaptureStatusDoc | null | undefined>(undefined)
  const [days, setDays] = useState<DaysSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const key = `${orgId}|${period}|${attempt}`

  useEffect(() => { logFeature('benefits_capture') }, [])

  useEffect(() => {
    return subscribeBenefitsCaptureStatus(orgId, setStatus, (err) => {
      Sentry.captureException(err)
      setError(LOAD_ERROR)
    })
  }, [orgId, attempt])

  useEffect(() => {
    if (status === undefined) return
    const st = status ?? EMPTY_STATUS
    // The clock the windows are computed from — one per subscription, so the
    // fold and the query agree; refreshed on every period change and retry.
    const clock = new Date()
    const to = latestYesterday(st, clock) ?? localDayOf(DEFAULT_TIMEZONE, clock)
    const from = fetchFrom(period, st, clock)
    return subscribeBenefitsCaptureDays(
      orgId, from, to,
      (docs) => setDays({ key, now: clock, docs }),
      (err) => {
        Sentry.captureException(err)
        setError(LOAD_ERROR)
      },
    )
  }, [orgId, period, status, attempt, key])

  const current = days && days.key === key ? days : null
  const result = useMemo(() => {
    if (status === undefined || current === null) return null
    return foldPeriod({ period, status: status ?? EMPTY_STATUS, docs: current.docs, now: current.now })
  }, [period, status, current])

  const loading = !error && result === null
  const renderNow = current?.now ?? new Date()
  const throughFallback = latestYesterday(status ?? EMPTY_STATUS, renderNow)
    ?? localDayOf(DEFAULT_TIMEZONE, renderNow)

  function retry() {
    setError(null)
    setStatus(undefined)
    setDays(null)
    setAttempt((a) => a + 1)
  }

  const sentence = PERIOD_SENTENCE[period]
  const thisWeek = result?.weeks.at(-1) ?? null

  // The Dashboard renders each view straight into `.dashboard-body`, the flex
  // row that carries the app's vertical scroll (so the scrollbar sits at the
  // window's edge, not inside a capped column). The view still supplies its
  // own `main.dashboard-main`: it is the centered column holding the page's
  // padding and width cap, which BenefitsCapture.css widens for this page.
  // Without it the page is a bare flex item stretched to the row's height.
  return (
    <main className="dashboard-main">
    <div className="benefits-capture">
      <header className="bc-head">
        <div className="bc-head__text">
          <span className="bc-kicker bc-kicker--accent">Medical benefits capture</span>
          <h1 className="bc-h1">Benefits Capture</h1>
          <p className="bc-lede">When a patient checks in, are their medical benefits pulled alongside vision?</p>
        </div>
        <div className="bc-seg" role="group" aria-label="Period">
          {(Object.keys(PERIOD_LABELS) as PeriodKey[]).map((k) => (
            <button key={k} className="bc-seg__opt" aria-pressed={period === k} onClick={() => setPeriod(k)}>
              {PERIOD_LABELS[k]}
            </button>
          ))}
        </div>
      </header>

      {error && (
        <div className="bc-error" role="alert">
          <span>{error} · data through {fmtDay(throughFallback)}</span>
          <button className="bc-btn" onClick={retry}>Try again</button>
        </div>
      )}

      {loading && <p className="bc-status">Loading your numbers…</p>}

      {!error && result && (
        <>
          {result.partial && (
            <p className="bc-partial">
              Partial data: {result.missingDays > 0
                ? `${fmt(result.missingDays)} clinic-day${result.missingDays === 1 ? '' : 's'} in this period ${result.missingDays === 1 ? 'is' : 'are'} not ready yet`
                : 'a day in this period exceeded the reporting cap'}
              {' '}— the numbers below are a floor, not a complete total. Data through {fmtDay(result.dataThrough ?? throughFallback)}.
            </p>
          )}

          {/* Hero */}
          <section className="bc-hero">
            <div className="bc-hero__glow" aria-hidden="true" />
            <div className={result.current.visits === 0 ? 'bc-hero__grid bc-hero__grid--solo' : 'bc-hero__grid'}>
              <div className="bc-hero__main">
                <span className="bc-kicker bc-kicker--hero">Medical benefits captured at check-in</span>
                <div className="bc-hero__row">
                  {result.current.visits === 0 ? (
                    <>
                      <span className="bc-hero__big bc-hero__big--empty">No visits</span>
                      <span className="bc-hero__of">· {sentence}</span>
                    </>
                  ) : (
                    <>
                      <span className="bc-hero__big">{fmtPct(result.hero.share)}</span>
                      <span className="bc-hero__of">of {fmt(result.current.visits)} check-ins · {sentence}</span>
                    </>
                  )}
                </div>
                {/* The prototype's "+N pts since go-live" and "Started at X%"
                    chips need a pre-VitalRev baseline no practice has recorded;
                    v1 carries no baseline mechanism, so the chips are omitted
                    rather than shown as dashes. */}
              </div>
              {/* Without visits every stat is a zero or a dash, so the column is not shown. */}
              {result.current.visits > 0 && (
                <div className="bc-hero__stats">
                  <HeroStat
                    value={fmt(result.hero.visionOnly)}
                    label="vision-only check-ins — medical not verified"
                    delta={deltaText(result.hero.visionOnlyDelta, period, 'bare')}
                  />
                  <HeroStat
                    value={fmtPct(result.activeRate.share)}
                    label="of medical checks find active coverage"
                    delta={deltaText(result.activeRate.deltaPts, period, 'pts')}
                  />
                  <HeroStat
                    value={result.recoverable.estimate === null ? DASH : fmt(result.recoverable.estimate)}
                    badge
                    label="covered visits missed — recoverable at check-in"
                    delta={deltaText(result.recoverable.delta, period, 'bare')}
                  />
                </div>
              )}
            </div>
          </section>

          {/* Where check-ins land */}
          <section className="bc-section">
            <span className="bc-kicker">Where check-ins land · {sentence}</span>
            <div className="bc-card bc-funnel">
              <Tile
                fill="neutral" bar={result.current.visits > 0 ? 100 : 0}
                value={result.tiles.visits.count} pctLabel={null}
                label="Patient check-ins"
                delta={deltaText(result.tiles.visits.delta, period, 'visits')}
                note="all scheduled visits seen"
              />
              <Tile
                fill="neutral-2" bar={result.tiles.visionVerified.share}
                value={result.tiles.visionVerified.count}
                pctLabel={shareLabel(result.tiles.visionVerified.share, 'check-ins')}
                label="Vision benefits verified"
                delta={deltaText(result.tiles.visionVerified.deltaPts, period, 'pts')}
                note="vision capture is a solved habit"
              />
              <Tile
                fill="accent" bar={result.tiles.medicalVerified.share}
                value={result.tiles.medicalVerified.count}
                pctLabel={shareLabel(result.tiles.medicalVerified.share, 'check-ins')}
                label="Medical benefits verified"
                delta={deltaText(result.tiles.medicalVerified.deltaPts, period, 'pts')}
                note={`${fmt(result.tiles.medicalVerified.noAnswer)} check-ins with no medical answer`}
                noteAccent
              />
              <Tile
                fill="accent-2" bar={result.tiles.activeCoverage.bar}
                value={result.tiles.activeCoverage.count}
                pctLabel={shareLabel(result.tiles.activeCoverage.rate, 'medical checks')}
                label="Active medical coverage found"
                delta={deltaText(result.tiles.activeCoverage.deltaPts, period, 'pts')}
                note="billable medical benefits confirmed"
              />
            </div>
          </section>

          {/* Progress since go-live */}
          <section className="bc-section">
            <span className="bc-kicker">Progress since go-live</span>
            <div className="bc-card bc-chart-card">
              <div className="bc-chart-head">
                <div className="bc-chart-head__text">
                  <div className="bc-card-title">Weekly medical verification rate</div>
                  <span className="bc-sub">
                    {thisWeek && thisWeek.share !== null
                      ? `${thisWeek.share}% this week · ${fmt(thisWeek.verified)} of ${fmt(thisWeek.visits)} check-ins so far`
                      : 'No check-ins yet this week'}
                    {' · '}every clinic pooled, Monday to Sunday
                  </span>
                </div>
                {thisWeek && <span className="bc-tag bc-tag--neutral">Week of {fmtDay(thisWeek.start, false)}</span>}
              </div>
              <WeeklyChart weeks={result.weeks} goal={frontDeskGoal} />
              {result.weeks.some((w) => w.visits > 0) && (
                <p className="bc-foot">The dashed line is the practice's own goal for its front desk, not a number VitalRev promises.</p>
              )}
            </div>
          </section>

          {/* Where it's lagging */}
          <section className="bc-section">
            <span className="bc-kicker">Where it's lagging · {sentence}</span>
            <div className="bc-lag">
              <div className="bc-card bc-lag__clinics">
                <div className="bc-card-title">By clinic</div>
                <div className="bc-table-wrap">
                  <table className="bc-table">
                    <thead>
                      <tr>
                        <th>Clinic</th>
                        <th className="bc-right">Check-ins</th>
                        <th className="bc-right">Now</th>
                        <th className="bc-right">Since start</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.clinics.map((c) => (
                        <tr key={c.clinicId}>
                          <td>
                            <span className="bc-clinic">
                              {c.name}
                              {c.archived && <span className="bc-tag bc-tag--outline-muted">archived</span>}
                              {c.lagging && (
                                <span className="bc-tag bc-tag--outline" title={`10 or more points under the practice's ${fmtPct(result.hero.share)} on at least 20 check-ins`}>
                                  Lagging
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="bc-right">{fmt(c.visits)}</td>
                          <td className="bc-right bc-strong">{fmtPct(c.share)}</td>
                          <td className="bc-right bc-dim" title={NO_BASELINE_TIP}>{DASH}</td>
                        </tr>
                      ))}
                      {result.clinics.length === 0 && (
                        <tr><td colSpan={4} className="bc-dim">No clinics yet — the rows appear as soon as the practice's clinics are set up.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="bc-foot">Rate = check-ins where medical benefits were pulled alongside vision, automatically at eligibility.</p>
              </div>
              <div className="bc-card bc-lag__reasons">
                <div className="bc-card-title">Why medical wasn't verified</div>
                <p className="bc-foot">
                  Check-ins where the automatic medical check couldn't run or came back unverified
                  {result.tiles.medicalVerified.noAnswer > 0 ? ` (${fmt(result.tiles.medicalVerified.noAnswer)})` : ''}.
                </p>
                {/* Only reasons that happened; a reason at zero says nothing. */}
                {result.current.visits === 0 ? (
                  <div className="bc-reasons bc-reasons--empty">No checks</div>
                ) : result.tiles.medicalVerified.noAnswer === 0 ? (
                  <div className="bc-reasons bc-reasons--empty">All checks passed</div>
                ) : (
                  <div className="bc-reasons">
                    {result.reasons.filter((r) => r.count > 0).map((r) => {
                      const copy = REASON_COPY[r.key]
                      return (
                        <div key={r.key} className="bc-reason" title={copy.hint}>
                          <span className="bc-reason__name">{copy.label}</span>
                          <span className="bc-reason__nums">
                            <span className="bc-reason__count">{fmt(r.count)}</span>
                            <span className="bc-reason__pct">{r.share}%</span>
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
    </main>
  )
}
